import { useEffect, useState } from "react";
import { toast } from "react-toastify";
import api from "../services/api";
import SearchableSelect from "./SearchableSelect";
import Pagination from "./Pagination";
import { fetchAvailability, formatQty } from "../utils/stockCheck";

const TYPES = [
  { value: "STOCK_IN", label: "Stock In", hint: "Goods received into a warehouse (increases its stock)." },
  { value: "STOCK_OUT", label: "Stock Out", hint: "Goods issued out of a warehouse (decreases its stock)." },
  { value: "TRANSFER", label: "Transfer", hint: "Goods moved from one warehouse to another (out of the source, into the destination)." },
];
const typeLabel = (type) => TYPES.find((t) => t.value === type)?.label || type;
const emptyForm = { type: "TRANSFER", productId: "", quantity: "", fromWarehouseId: "", toWarehouseId: "", transferDate: "", reason: "" };
const PAGE_SIZE = 25;

/** Stock in / stock out / transfer between warehouses, with the list of past entries. */
const StockTransfers = () => {
  const [products, setProducts] = useState([]);
  const [warehouses, setWarehouses] = useState([]);
  const [form, setForm] = useState(emptyForm);
  const [stock, setStock] = useState(null);
  const [saving, setSaving] = useState(false);
  const [rows, setRows] = useState([]);
  const [page, setPage] = useState(0);
  const [totalPages, setTotalPages] = useState(0);
  const [totalElements, setTotalElements] = useState(0);
  const [filterType, setFilterType] = useState("");

  useEffect(() => {
    api.get("/api/v1/products", { size: 2000 }).then((d) => setProducts((d?.content || []).filter((p) => p.active !== false))).catch(() => {});
    api.get("/api/v1/warehouses/all").then((d) => setWarehouses(Array.isArray(d) ? d : [])).catch(() => {});
  }, []);

  const loadList = async () => {
    try {
      const data = await api.get("/api/v1/inventory/transfers", { page, size: PAGE_SIZE, sort: "id,desc", type: filterType || undefined });
      setRows(data?.content || []);
      setTotalPages(data?.totalPages ?? 0);
      setTotalElements(data?.totalElements ?? 0);
    } catch (error) {
      toast.error(error.message || "Failed to load stock transfers");
    }
  };

  useEffect(() => {
    loadList();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [page, filterType]);

  useEffect(() => {
    if (!form.productId) {
      setStock(null);
      return;
    }
    fetchAvailability([form.productId])
      .then((map) => setStock(map[form.productId] || null))
      .catch(() => setStock(null));
  }, [form.productId]);

  const set = (field, value) => setForm((prev) => ({ ...prev, [field]: value }));
  const heldIn = (warehouseId) => (stock && warehouseId ? stock.warehouses.find((w) => String(w.warehouseId) === String(warehouseId))?.quantity ?? 0 : null);

  const submit = async (e) => {
    e.preventDefault();
    if (!form.productId) return toast.error("Select a product");
    if (!(Number(form.quantity) > 0)) return toast.error("Enter a quantity");
    if (form.type !== "STOCK_IN" && !form.fromWarehouseId) return toast.error("Select the source warehouse");
    if (form.type !== "STOCK_OUT" && !form.toWarehouseId) return toast.error("Select the destination warehouse");
    if (form.type === "TRANSFER" && form.fromWarehouseId === form.toWarehouseId) return toast.error("Source and destination must differ");
    if (!form.reason.trim()) return toast.error("Enter the reason");
    if (form.type !== "STOCK_IN" && Number(form.quantity) > Number(heldIn(form.fromWarehouseId) || 0))
      return toast.error(`Only ${formatQty(heldIn(form.fromWarehouseId))} in the source warehouse`);
    setSaving(true);
    try {
      await api.post("/api/v1/inventory/transfers", {
        type: form.type,
        productId: Number(form.productId),
        quantity: Number(form.quantity),
        fromWarehouseId: form.type === "STOCK_IN" ? null : Number(form.fromWarehouseId),
        toWarehouseId: form.type === "STOCK_OUT" ? null : Number(form.toWarehouseId),
        transferDate: form.transferDate || null,
        reason: form.reason.trim(),
      });
      toast.success(`${typeLabel(form.type)} saved; stock updated`);
      setForm((prev) => ({ ...emptyForm, type: prev.type, fromWarehouseId: prev.fromWarehouseId, toWarehouseId: prev.toWarehouseId }));
      setPage(0);
      loadList();
    } catch (error) {
      toast.error(error.message || "Could not save");
    } finally {
      setSaving(false);
    }
  };

  const warehouseOptions = (exclude) =>
    warehouses
      .filter((w) => String(w.id) !== String(exclude || ""))
      .map((w) => (
        <option key={w.id} value={w.id}>
          {w.name}
          {w.type === "BAD" ? " (bad stock)" : ""}
          {stock ? ` — ${formatQty(heldIn(w.id))}` : ""}
        </option>
      ));

  return (
    <div className="p-4" style={{ position: "relative" }}>
      <div className="page-subtitle" style={{ marginBottom: 12 }}>
        Stock in increases the quantity in the selected warehouse, stock out decreases it, and a transfer moves it from the source to the
        destination warehouse. Every entry is recorded in the stock ledger with its reason.
      </div>
      <form onSubmit={submit} className="page-panel" style={{ padding: "14px 16px" }}>
        <div className="filters-bar" style={{ marginTop: 0 }}>
          {TYPES.map((t) => (
            <label key={t.value} style={{ display: "inline-flex", gap: 6, alignItems: "center" }}>
              <input type="radio" name="stock-transfer-type" checked={form.type === t.value} onChange={() => set("type", t.value)} />
              {t.label}
            </label>
          ))}
          <span className="page-subtitle">{TYPES.find((t) => t.value === form.type)?.hint}</span>
        </div>
        <div className="form-grid">
          <div className="form-group" style={{ gridColumn: "1 / -1" }}>
            <label>Product *</label>
            <SearchableSelect
              value={form.productId}
              onChange={(value) => set("productId", value)}
              options={products.map((p) => ({ value: p.id, label: p.name, sublabel: `${p.skuCode} · ${p.uom}` }))}
              placeholder="Search product..."
              required
            />
            {stock ? (
              <div className="page-subtitle" style={{ marginTop: 4 }}>
                Stock:{" "}
                {stock.warehouses.map((w) => `${w.warehouseName} ${formatQty(w.quantity)}`).join(" · ")} (good total {formatQty(stock.total)} {stock.uom})
              </div>
            ) : null}
          </div>
          <div className="form-group">
            <label>Quantity *</label>
            <input type="number" min="0" step="0.001" value={form.quantity} onChange={(e) => set("quantity", e.target.value)} required />
          </div>
          {form.type !== "STOCK_IN" ? (
            <div className="form-group">
              <label>Source Warehouse *</label>
              <select value={form.fromWarehouseId} onChange={(e) => set("fromWarehouseId", e.target.value)} required>
                <option value="">Select source</option>
                {warehouseOptions(form.type === "TRANSFER" ? form.toWarehouseId : null)}
              </select>
            </div>
          ) : null}
          {form.type !== "STOCK_OUT" ? (
            <div className="form-group">
              <label>Destination Warehouse *</label>
              <select value={form.toWarehouseId} onChange={(e) => set("toWarehouseId", e.target.value)} required>
                <option value="">Select destination</option>
                {warehouseOptions(form.type === "TRANSFER" ? form.fromWarehouseId : null)}
              </select>
            </div>
          ) : null}
          <div className="form-group">
            <label>Date</label>
            <input type="date" value={form.transferDate} onChange={(e) => set("transferDate", e.target.value)} />
          </div>
          <div className="form-group" style={{ gridColumn: "1 / -1" }}>
            <label>Reason for {typeLabel(form.type).toLowerCase()} *</label>
            <input value={form.reason} onChange={(e) => set("reason", e.target.value)} maxLength={500} required placeholder="e.g. Shifted to godown 2, opening stock, damaged" />
          </div>
        </div>
        <div className="modal-actions">
          <button type="submit" className="primary" disabled={saving}>
            {saving ? "Saving..." : `Save ${typeLabel(form.type)}`}
          </button>
        </div>
      </form>

      <div className="filters-bar" style={{ marginTop: 18 }}>
        <h3 style={{ fontWeight: 600, margin: 0 }}>Entries</h3>
        <select value={filterType} onChange={(e) => { setFilterType(e.target.value); setPage(0); }} aria-label="Type">
          <option value="">All types</option>
          {TYPES.map((t) => (
            <option key={t.value} value={t.value}>
              {t.label}
            </option>
          ))}
        </select>
      </div>
      <div className="table-wrapper">
        <table>
          <thead>
            <tr>
              <th>#</th>
              <th>Date</th>
              <th>Type</th>
              <th>Product</th>
              <th style={{ textAlign: "right" }}>Qty</th>
              <th>From</th>
              <th>To</th>
              <th>Reason</th>
              <th>By</th>
            </tr>
          </thead>
          <tbody>
            {rows.length === 0 ? (
              <tr>
                <td colSpan={9}>No stock transfers yet.</td>
              </tr>
            ) : (
              rows.map((row) => (
                <tr key={row.id}>
                  <td>{row.id}</td>
                  <td>{row.transferDate}</td>
                  <td>
                    <span className={`badge ${row.type === "STOCK_IN" ? "badge-green" : row.type === "STOCK_OUT" ? "badge-red" : "badge-blue"}`}>
                      {typeLabel(row.type)}
                    </span>
                  </td>
                  <td>
                    {row.skuCode} {row.productName}
                  </td>
                  <td style={{ textAlign: "right" }}>{formatQty(row.quantity)}</td>
                  <td>{row.fromWarehouseName || "-"}</td>
                  <td>{row.toWarehouseName || "-"}</td>
                  <td style={{ whiteSpace: "normal" }}>{row.reason}</td>
                  <td>{row.createdBy}</td>
                </tr>
              ))
            )}
          </tbody>
        </table>
      </div>
      <Pagination page={page} totalPages={totalPages} totalElements={totalElements} onPageChange={setPage} />
    </div>
  );
};

export default StockTransfers;
